import { ChangeDetectionStrategy, Component, ViewEncapsulation, effect } from '@angular/core'
import { map, BehaviorSubject, filter, take, switchMap, distinctUntilChanged, pairwise } from 'rxjs'
import { Router } from '@angular/router'
import { GbSetupGuideProgressService } from 'libs/gb-dashboard/setup-guide/utils/src/lib/gb-setup-guide-progress.service'
import {
  SetupGuideStepStatusEnum,
  SetupGuideStep,
  SetupGuideSubStepKeyEnum,
} from 'libs/gb-dashboard/setup-guide/utils/src/lib/setup-guide.models'
import { CampaignPreferencesFacade } from 'libs/gb-dashboard/shared/data-access-campaign-preferences/src/lib/+state/campaign-preferences.facade'
import * as CampaignPreferencesActions from 'libs/gb-dashboard/shared/data-access-campaign-preferences/src/lib/+state/campaign-preferences.actions'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { GbSetupGuideCompleteDialogComponent } from '../gb-setup-guide-complete-dialog/gb-setup-guide-complete-dialog.component'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { SetupConstants } from 'libs/gb-dashboard/setup/feature/src/lib/setup.constants'

@UntilDestroy()
@Component({
  selector: 'gb-setup-guide-sidebar',
  standalone: false,
  templateUrl: './gb-setup-guide-sidebar.component.html',
  styleUrl: './gb-setup-guide-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbSetupGuideSidebarComponent {
  protected readonly SetupConstants = SetupConstants
  activeTab: typeof SetupConstants.SETUP | typeof SetupConstants.AI = SetupConstants.SETUP

  enableAiAssistant$ = this.selectedAuctionFacade.auction$.pipe(map((auction) => auction?.enable_ai_assistant ?? false))

  private stepsSubject = new BehaviorSubject<SetupGuideStep[]>([])
  private lastProcessedUrl: string | null = null
  steps$ = this.stepsSubject.asObservable()
  shouldShowSetupWithAI$ = this.steps$.pipe(
    map((steps) => {
      const expandedStep = steps.find((step) => step.expanded)
      return expandedStep ? expandedStep.shouldShowSetupWithAI : false
    }),
  )
  autoFillFormType$ = this.steps$.pipe(
    map((steps) => {
      const expandedStep = steps.find((step) => step.expanded)
      return expandedStep ? expandedStep.autoFillFormType : undefined
    }),
  )
  aiPromptPlaceholder$ = this.steps$.pipe(
    map((steps) => {
      const expandedStep = steps.find((step) => step.expanded)
      return expandedStep ? expandedStep.aiPromptPlaceholder : undefined
    }),
  )

  totalTasks$ = this.steps$.pipe(map((steps) => steps?.length ?? 0))

  completedTasks$ = this.steps$.pipe(
    map((steps) => (steps ?? []).filter((s) => s.status === SetupGuideStepStatusEnum.COMPLETED).length),
  )

  allStepsComplete$ = this.steps$.pipe(
    map((steps) => !!steps?.length && steps.every((s) => s.status === SetupGuideStepStatusEnum.COMPLETED)),
  )

  constructor(
    private gbSetupGuideProgressService: GbSetupGuideProgressService,
    private campaignPreferencesFacade: CampaignPreferencesFacade,
    private gbDashboardDialogService: GbDashboardDialogService,
    private router: Router,
    private selectedAuctionFacade: SelectedAuctionFacade,
  ) {
    effect(() => {
      const recentlyStruckOff = this.gbSetupGuideProgressService.recentlyStruckOffSubStep()
      if (recentlyStruckOff) {
        this.updateSubStepStatus(recentlyStruckOff, SetupGuideStepStatusEnum.COMPLETED)
      }
    })

    effect(() => {
      const recentlyUndoStruckOff = this.gbSetupGuideProgressService.recentlyUndoStruckOffSubStep()
      if (recentlyUndoStruckOff) {
        this.updateSubStepStatus(recentlyUndoStruckOff, SetupGuideStepStatusEnum.PENDING)
      }
    })

    this.subscribeToStepUpdates()
    this.watchSetupGuideComplete()
  }

  private subscribeToStepUpdates(): void {
    this.gbSetupGuideProgressService
      .getStepsWithUpdatedProgress$()
      .pipe(untilDestroyed(this))
      .subscribe((steps) => {
        const currentUrl = this.router.url.split('?')[0]

        const baseSteps =
          this.lastProcessedUrl === currentUrl
            ? steps
            : (() => {
                this.lastProcessedUrl = currentUrl
                return this.gbSetupGuideProgressService.setExpandedStep(steps, currentUrl)
              })()

        this.stepsSubject.next(this.applyStruckOffOverrides(baseSteps))
      })
  }

  /**
   * Preserve in-memory struck-off sub-step state across observable re-emissions
   * so unsaved local progress (e.g. typed event dates) isn't overwritten by
   * a re-evaluation against the persisted auction.
   */
  private applyStruckOffOverrides(steps: SetupGuideStep[]): SetupGuideStep[] {
    const struckOff = this.gbSetupGuideProgressService.getStruckOffSubSteps()
    if (struckOff.size === 0) return steps

    return steps.map((step) => {
      if (!step.subSteps?.length) return step

      let mutated = false
      const updatedSubSteps = step.subSteps.map((subStep) => {
        if (
          struckOff.has(subStep.key as SetupGuideSubStepKeyEnum) &&
          subStep.status !== SetupGuideStepStatusEnum.COMPLETED
        ) {
          mutated = true
          return { ...subStep, status: SetupGuideStepStatusEnum.COMPLETED }
        }
        return subStep
      })

      if (!mutated) return step

      const allSubStepsCompleted = updatedSubSteps.every(
        (subStep) => subStep.status === SetupGuideStepStatusEnum.COMPLETED,
      )

      return {
        ...step,
        subSteps: updatedSubSteps,
        status:
          step.status === SetupGuideStepStatusEnum.SKIPPED
            ? SetupGuideStepStatusEnum.SKIPPED
            : allSubStepsCompleted
              ? SetupGuideStepStatusEnum.COMPLETED
              : step.status,
      }
    })
  }

  /**
   * Opens the "Setup Complete" dialog when:
   * - Setup guide transitions from incomplete to complete (false -> true)
   * - All other dialogs (e.g. sub-step dialogs) are closed
   */
  private watchSetupGuideComplete(): void {
    this.campaignPreferencesFacade.setupGuideComplete$
      .pipe(
        distinctUntilChanged(),
        pairwise(),
        filter(([prev, curr]) => !prev && curr), // only when transitioning false -> true
        take(1),
        switchMap(() => this.gbDashboardDialogService.afterAllClosed$.pipe(take(1))),
        untilDestroyed(this),
      )
      .subscribe(() => this.gbDashboardDialogService.openDialog(GbSetupGuideCompleteDialogComponent))
  }

  private updateSubStepStatus(subStepKey: SetupGuideSubStepKeyEnum, status: SetupGuideStepStatusEnum): void {
    const currentSteps = this.stepsSubject.value

    const stepIndex = currentSteps.findIndex((step) => step.subSteps?.some((subStep) => subStep.key === subStepKey))

    if (stepIndex === -1) return

    const step = currentSteps[stepIndex]

    const updatedSubSteps = step.subSteps?.map((subStep) => {
      if (subStep.key === subStepKey) {
        return {
          ...subStep,
          status,
        }
      }
      return subStep
    })

    let stepStatus = step.status
    if (status === SetupGuideStepStatusEnum.COMPLETED) {
      const allSubStepsCompleted = updatedSubSteps?.every(
        (subStep) => subStep.status === SetupGuideStepStatusEnum.COMPLETED,
      )
      stepStatus = allSubStepsCompleted ? SetupGuideStepStatusEnum.COMPLETED : step.status
    } else {
      const anySubStepPending = updatedSubSteps?.some((subStep) => subStep.status === SetupGuideStepStatusEnum.PENDING)
      stepStatus = anySubStepPending ? SetupGuideStepStatusEnum.PENDING : SetupGuideStepStatusEnum.COMPLETED
    }

    const updatedSteps = [...currentSteps]
    updatedSteps[stepIndex] = {
      ...step,
      subSteps: updatedSubSteps,
      status: stepStatus,
    }

    this.stepsSubject.next(updatedSteps)
  }

  closeSidebar(): void {
    this.gbDashboardDialogService
      .openConfirmDialog({
        title: 'SETUP_GUIDE.CONFIRM_CLOSE.TITLE',
        body: 'SETUP_GUIDE.CONFIRM_CLOSE.BODY',
        confirmButtonText: 'OK',
        cancelButtonText: 'CANCEL',
      })
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res) {
          this.campaignPreferencesFacade.dispatch(CampaignPreferencesActions.setSetupGuideDrawerState({ closed: true }))
        }
      })
  }

  dismissSidebar(): void {
    this.campaignPreferencesFacade.dispatch(CampaignPreferencesActions.setSetupGuideDrawerState({ closed: true }))
  }

  setActiveTab(tab: typeof SetupConstants.SETUP | typeof SetupConstants.AI): void {
    this.activeTab = tab
  }
}
